import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

export type CardMark = 'visa' | 'mastercard' | 'amex' | 'applepay' | 'googlepay';

/**
 * The payment marks a shopper looks for - card brands and wallets - drawn as small SVG badges
 * instead of a line of text ("Visa · Mastercard · Amex..." read like a list, not a checkout; the owner, 2026-09-26).
 * Drawn here, not loaded: no request, nothing to fail, the same on the server render. `set="cards"` draws the cards
 * (all five, or `only` those named).
 */
@Component({
    selector: 'app-payment-marks',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
<span class="pmarks">
  <ng-container *ngIf="set === 'cards'">
    <svg *ngIf="shows('visa')" class="pmarks__card" [attr.height]="height" [attr.width]="cardWidth" viewBox="0 0 38 24" role="img" aria-label="Visa">
      <rect x=".5" y=".5" width="37" height="23" rx="3.5" fill="#fff" stroke="#D9DEE3"/>
      <text x="19" y="16.2" text-anchor="middle" font-family="Arial Black, Arial, Helvetica, sans-serif" font-size="11"
            font-weight="900" font-style="italic" letter-spacing="-.3" fill="#1A1F71">VISA</text>
    </svg>
    <svg *ngIf="shows('mastercard')" class="pmarks__card" [attr.height]="height" [attr.width]="cardWidth" viewBox="0 0 38 24" role="img" aria-label="Mastercard">
      <rect x=".5" y=".5" width="37" height="23" rx="3.5" fill="#fff" stroke="#D9DEE3"/>
      <circle cx="15" cy="12" r="7" fill="#EB001B"/>
      <circle cx="23" cy="12" r="7" fill="#F79E1B"/>
      <path d="M19 6.255A7 7 0 0 1 19 17.745A7 7 0 0 1 19 6.255Z" fill="#FF5F00"/>
    </svg>
    <svg *ngIf="shows('amex')" class="pmarks__card" [attr.height]="height" [attr.width]="cardWidth" viewBox="0 0 38 24" role="img" aria-label="American Express">
      <rect width="38" height="24" rx="3.5" fill="#1F72CD"/>
      <text x="19" y="15.6" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="9.5"
            font-weight="800" letter-spacing=".4" fill="#fff">AMEX</text>
    </svg>
    <svg *ngIf="shows('applepay')" class="pmarks__card" [attr.height]="height" [attr.width]="cardWidth" viewBox="0 0 38 24" role="img" aria-label="Apple Pay">
      <rect width="38" height="24" rx="3.5" fill="#000"/>
      <path transform="translate(5.2 5.6) scale(.5)" fill="#fff"
            d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
      <text x="17.2" y="16.3" font-family="-apple-system, 'Segoe UI', Arial, Helvetica, sans-serif" font-size="10"
            font-weight="600" fill="#fff">Pay</text>
    </svg>
    <svg *ngIf="shows('googlepay')" class="pmarks__card" [attr.height]="height" [attr.width]="cardWidth" viewBox="0 0 38 24" role="img" aria-label="Google Pay">
      <rect x=".5" y=".5" width="37" height="23" rx="3.5" fill="#fff" stroke="#D9DEE3"/>
      <path transform="translate(5.4 6.6) scale(.45)" fill="#4285F4"
            d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/>
      <text x="17.4" y="16.3" font-family="'Google Sans', Roboto, Arial, Helvetica, sans-serif" font-size="10"
            font-weight="500" fill="#5F6368">Pay</text>
    </svg>
  </ng-container>

</span>`,
    styles: [`
.pmarks { display: inline-flex; align-items: center; flex-wrap: wrap; gap: 4px; vertical-align: middle; line-height: 1; }
.pmarks__card { display: block; flex: 0 0 auto; }
`],
})
export class PaymentMarksComponent {
    /// Which marks: the card brands and wallets.
    @Input() set: 'cards' = 'cards';
    /// For the cards: only these (the footer shows the three card brands); all five when empty.
    @Input() only: CardMark[] | null = null;
    /// Height of a mark in pixels.
    @Input() height = 24;

    /// A card mark keeps the 38 x 24 shape of a payment card.
    get cardWidth(): number {
        return Math.round(this.height * 38 / 24);
    }

    shows(mark: CardMark): boolean {
        return !this.only || this.only.length === 0 || this.only.indexOf(mark) >= 0;
    }
}
